import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback
} from "react";
import { FaBackward, FaSpinner } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import {
  agreementMailPages,
  agreementTexts,
  buttonName,
  commonErrMsg,
  commonValidationMsg,
  identityText,
  mail,
  optimizationTexts,
  ownerTab,
  pagination,
  tooltips
} from "../../../locales/en";
import MultiStepFormContext from "../../../components/MultiStepForm/MultiStepFormContext";
import publicAxios from "../../../utils/publicAxios";
import { showToast } from "../../../utils/utils";
import {
  ADDRESSFINDERKEY,
  MAILTO_INFO_EMAIL,
  MAX_CALL_COUNT,
  PROPERTY_OWNERSHIP_CHOOSE,
  PROPERTY_OWNERSHIP_OTHER_PROPERTY,
  SCHEDULED_SECOND_FIVE,
  SCHEDULED_SECOND_TEN,
  showToastType
} from "../../../utils/constants/appConstants";
import { FILE_SIZE_LIMIT } from "../../../utils/constants/appConstants";
import {
  getAccAnnatureStatusURL,
  getAgreementUploadUrlLaterIDTokenUrl,
  getCountryCodeUrl,
  getLeagalAgreementDownloadTokenUrl,
  getLeagalAgreementUploadTokenUrl,
  getOnboardGuaranteeFormURL,
  getOwnerPropertyAddressTokenUrl
} from "../../../utils/constants/apiEndPoints";
import { WidgetInput } from "react-addressfinder";
import { pr_ownership } from "../../../utils/data/data";
import { useSelector } from "react-redux";
import AgreementModalId from "../../../components/modals/AgreementModalNoID";

const Agreement = () => {
  const { prev } = useContext(MultiStepFormContext);
  const [showManually, setShowManually] = useState();
  const [showSubmit, setSubmit] = useState();
  const [addAgreementModal, setAddAgreementModal] = useState(false);
  const [searchParams] = useSearchParams();

  const [getToken, setGetToken] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [isUploadLater, setIsUploadLater] = useState(false);
  const fileInput = useRef(null);
  const { applyIdDataToken } = useSelector((state) => state.agreementPages);
  const [annatureStatusData, setAnnatureStatusData] = useState(null);
  const [data, setData] = useState(null);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [anatureDataUrl, setAnatureDataUrl] = useState(null);
  const [anatureError, setAnatureError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [anatureLoading, setAnatureLoading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const handleDownload = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoading(true);
      const response = await publicAxios.get(
        getLeagalAgreementDownloadTokenUrl,
        {
          responseType: "arraybuffer",
          params: {
            token: getToken
          }
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });

      // Create a link element
      const link = document.createElement("a");

      // Set the download attribute with the filename from the response headers
      const filename = "FundTap Deed.pdf";
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      // Trigger the download by simulating a click on the link
      link.click();

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch {
      showToast(commonErrMsg.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const { files } = event.target;

    // Ensure a file is selected
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(commonValidationMsg?.fileLimitError, showToastType.error);
      event.target.value = "";
      setFileName("");
      return;
    }

    setFileName(file.name);

    try {
      setLoadingUpload(true);

      const formDataToSend = new FormData();
      formDataToSend.append("file", file, file.name);

      const config = {
        method: "post",
        url: getLeagalAgreementUploadTokenUrl,
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: {
          token: getToken
        },
        data: formDataToSend
      };

      const response = await publicAxios(config);

      if (response.status === 200) {
        showToast(
          response.data.message ?? optimizationTexts?.fileUploaded,
          showToastType.success
        );
        setIsUploaded(true);
      }
    } catch (error) {
      if (error.response) {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInput.current.click();
  };

  const handleCheckboxChange = async (e) => {
    const { checked, type, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setIsUploadLater(newValue);

    try {
      const response = await publicAxios.post(
        getAgreementUploadUrlLaterIDTokenUrl,
        {},
        {
          params: {
            isUploadLater: newValue,
            token: getToken
          }
        }
      );

      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const toggleShowManually = () => {
    setShowManually(!showManually);
    setSubmit(true);
  };

  const handleBackToDigitalClick = () => {
    setShowManually(false);
    setSubmit(false);
  };

  const initialPropertyOwnership = {
    propertyOwnership: PROPERTY_OWNERSHIP_CHOOSE,
    propertyOwnedAdddress: ""
  };
  const [propertyOwnership, setPropertyOwnership] = useState(
    initialPropertyOwnership
  );
  const isOtherPropertyOwner =
    propertyOwnership.propertyOwnership === PROPERTY_OWNERSHIP_OTHER_PROPERTY;

  const savePropertyOwnership = async (data) => {
    try {
      await publicAxios.put(getOwnerPropertyAddressTokenUrl, data, {
        params: { token: getToken }
      });
    } catch (error) {
      showToast(
        error.response?.data?.message || commonErrMsg?.item1,
        showToastType.error
      );
    }
  };

  const handleOnChange = async (e) => {
    const { value } = e.target;

    // A new answer starts the address over: only "I own other property"
    // carries one, and the service drops a stored address for the rest.
    setPropertyOwnership({
      propertyOwnership: value,
      propertyOwnedAdddress: ""
    });

    await savePropertyOwnership({
      propertyOwnership: value === PROPERTY_OWNERSHIP_CHOOSE ? null : value
    });
  };

  // Typing is kept locally and saved once an address is picked from the
  // suggestions, so a half-typed address is never filed against the account.
  const handleManualChange = (e) => {
    setPropertyOwnership((prevData) => ({
      ...prevData,
      propertyOwnedAdddress: e.target.value
    }));
  };

  const [successMsg, setSuccessMsg] = useState(false);

  const handleConfirm = () => {
    setSuccessMsg(true);
  };

  const handleSelectSuggestionAddress = async (address) => {
    const updatedData = {
      ...propertyOwnership,
      propertyOwnedAdddress: address
    };

    setPropertyOwnership(updatedData);
    await savePropertyOwnership(updatedData);
  };

  // Country Code Fetch=====>
  const [countryCode, setCountryCode] = useState("");

  const getCountryCode = useCallback(async (getToken) => {
    try {
      const response = await publicAxios.get(getCountryCodeUrl, {
        params: { token: getToken }
      });
      setCountryCode(response?.data.data.primaryCountry);
    } catch {
      // handle catch here;
    }
  }, []);

  const loadGuranteeData = useCallback(async (getToken) => {
    try {
      setAnatureLoading(true);
      const response = await publicAxios.post(
        getOnboardGuaranteeFormURL,
        {},
        {
          params: {
            token: getToken
          }
        }
      );
      setData(response.data.data);
      setAnatureDataUrl(
        response?.data?.data?.signing_token_links?.[0]?.endpoint
      );
      // A document whose envelope has completed is reported as signed, so
      // the page says so instead of asking a signer to sign it again.
      setAnnatureStatusData(
        response?.data?.data?.is_signed
          ? {
              status: true,
              displayText: response?.data?.data?.signed_display_text
            }
          : null
      );
      // Reported by the backend when the envelope or its signing token could not
      // be obtained, so the modal names the failure instead of opening empty.
      setAnatureError(response?.data?.data?.signing_error || null);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || identityText?.tokenExpiredText
      );
    } finally {
      setAnatureLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    setGetToken(token);
    getCountryCode(token);
    loadGuranteeData(token);
  }, [searchParams]);

  // The completed state lands by webhook, so the poll that already runs when
  // the signing window closes re-reads this document - the same shape the
  // onboarding agreement step uses. A failed read leaves the page as it is;
  // the next poll, or the next visit, catches up.
  const refreshSignedState = useCallback(async () => {
    try {
      const response = await publicAxios.post(
        getOnboardGuaranteeFormURL,
        {},
        { params: { token: getToken } }
      );
      setData(response.data.data);
      if (response?.data?.data?.is_signed) {
        setAnnatureStatusData({
          status: true,
          displayText: response?.data?.data?.signed_display_text
        });
      }
    } catch {
      // leave the current state in place
    }
  }, [getToken]);

  // The account's Annature state, read alongside the document as a second
  // opinion: it is a plain status lookup, so it still answers when re-reading
  // the document itself fails. It needs a session, and it reports the account's
  // latest envelope rather than this token's document, so it may only ever
  // confirm a signature - never withdraw one the document itself reported.
  const handleAnnatureCheckStatus = useCallback(async () => {
    const userAccountId = data?.file_data?.userAccountId;
    if (!userAccountId) {
      return;
    }
    try {
      const response = await publicAxios.post(getAccAnnatureStatusURL, null, {
        params: { userAccountId }
      });
      if (response?.data?.data?.status) {
        setAnnatureStatusData(response.data.data);
      }
    } catch {
      // A recipient may open this link without a session, and the lookup is
      // secured: leave the page as it is rather than put an error in front of them.
    }
  }, [data]);

  const showAgreementModal = () => {
    if (addAgreementModal) {
      let callCount = 0;
      setIsDisabledButton(true);
      const scheduleNextCall = (delay) => {
        setTimeout(() => {
          callCount++;
          refreshSignedState();
          handleAnnatureCheckStatus();
          setIsDisabledButton(false);
          if (callCount < MAX_CALL_COUNT) {
            scheduleNextCall(SCHEDULED_SECOND_FIVE);
          }
        }, delay);
      };
      scheduleNextCall(SCHEDULED_SECOND_TEN);
    }
    setAddAgreementModal(!addAgreementModal);
  };

  // The signer is finished once the document is recorded as signed - the webhook
  // sets that flag when the envelope completes - or once they upload a signed copy.
  // The document section has nothing left to offer them either way.
  const isDocumentDone =
    Boolean(annatureStatusData?.status) ||
    Boolean(data?.file_data?.isDocumentSigned) ||
    isUploaded;

  const renderAgreementActions = () => {
    if (annatureStatusData?.status) {
      return <p className="text-success">{annatureStatusData?.displayText}</p>;
    }

    if (isDocumentDone) {
      return (
        <p className="text-success">
          {`${agreementTexts?.uploadDocsSuccessText} `}
          <a href={MAILTO_INFO_EMAIL}>{mail}</a>
        </p>
      );
    }

    // The backend's own wording, so the recipient sees what actually went
    // wrong; it falls back to the expired-link copy only for a failure that
    // carries no message.
    if (errorMessage) {
      return (
        <div className="expired-token-message text-center pt-5">
          <h3>{errorMessage}</h3>
        </div>
      );
    }

    return (
      <>
        <button
          type="button"
          className="btn btn-primary"
          onClick={showAgreementModal}
          disabled={isDisabledButton}
        >
          {agreementTexts?.reviewSign}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-transparent"
          onClick={toggleShowManually}
          disabled={isDisabledButton}
        >
          {agreementTexts?.signUpload}
        </button>
      </>
    );
  };

  if (successMsg) {
    return (
      <div className="alert alert-success mt-5 text-center" role="alert">
        {`${agreementMailPages?.addOwners.successText} `}
        <a href={MAILTO_INFO_EMAIL}>{mail}</a>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-md-8 offset-md-2">
          <div className="step-wrap">
            <div className="step-title">
              <h3>{agreementTexts?.tcGuarntee.title}</h3>
            </div>
            {applyIdDataToken?.status && (
              <div className="doYouOwn mt-3 row ">
                <div className="col-md-6 mb-3">
                  <label htmlFor="select" className="ms-1">
                    {agreementTexts?.tcGuarntee.doYouOwn}
                  </label>
                  <span className="text-danger">*</span> <br />
                </div>
                <div className="col-md-6 addowners-form">
                  <div className="input-wrap">
                    <select
                      id="select"
                      name="propertyOwnership"
                      value={
                        propertyOwnership.propertyOwnership ??
                        PROPERTY_OWNERSHIP_CHOOSE
                      }
                      onChange={handleOnChange}
                    >
                      {pr_ownership?.map((ownership) => (
                        <option value={ownership.value} key={ownership.id}>
                          {ownership.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {isOtherPropertyOwner && (
                  <>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="select" className="ms-1">
                        {ownerTab?.proprtyDetails.propertyOwnedAddrs}
                      </label>
                      <span className="text-danger">*</span> <br />
                    </div>
                    <div className="col-md-6 addowners-form">
                      <div className="input-wrap">
                        <WidgetInput
                          addressFinderKey={ADDRESSFINDERKEY}
                          id="propertyAddress"
                          country={countryCode}
                          name="propertyOwnedAdddress"
                          value={propertyOwnership.propertyOwnedAdddress}
                          onChange={handleManualChange}
                          onSelected={(address) => {
                            handleSelectSuggestionAddress(address);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="step-content mt-3">
              <div
                className={`${showManually && !isDocumentDone ? "hide" : "show"} hide-manually`}
              >
                {!isDocumentDone && (
                  <div className="aplyid-section">
                    <p>{agreementTexts?.pleaseReview}</p>
                  </div>
                )}

                <div className="agreement-btn-group mb-3">
                  {renderAgreementActions()}
                </div>
              </div>
              {showManually && !isDocumentDone && (
                <div className="show-manually-wrap mt-5">
                  <div className="show-manual-btn mb-5">
                    <button
                      type="button"
                      className="btn btn-primary btn-transparent"
                      onClick={handleBackToDigitalClick}
                    >
                      <FaBackward /> {agreementTexts?.reviewSign}
                    </button>
                  </div>
                  <div className="aplyid-section">
                    <p>{agreementTexts?.pleaseDownload}</p>
                    <p>{agreementTexts?.youComplete}</p>

                    <div className="agreement-group-warp mt-5">
                      <div className="row mb-2">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="Download">
                            {agreementTexts?.downloadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <Link
                            className={`btn btn-primary fundtap_agreement spinDiv`}
                            id="Download"
                            onClick={handleDownload}
                          >
                            {buttonName.download}
                            {loading && (
                              <span className="loadSpin">
                                <FaSpinner />
                              </span>
                            )}
                          </Link>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="uploadAgreement">
                            {agreementTexts?.uploadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="upload-file">
                            {fileName && (
                              <p className="uploaded-file-name">{fileName}</p>
                            )}
                            <button
                              type="button"
                              onClick={handleUploadButtonClick}
                              className={`btn btn-primary fundtap_agreement spinDiv`}
                            >
                              {tooltips.upload}
                              {loadingUpload && (
                                <span className="loadSpin">
                                  <FaSpinner />
                                </span>
                              )}
                            </button>
                            <input
                              name="isFile"
                              type="file"
                              ref={fileInput}
                              onChange={handleFileChange}
                              className="d-none"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 offset-md-6">
                          <div className="upload-checkbox">
                            <label htmlFor="upload">
                              {buttonName?.uploadLater}
                            </label>
                            <input
                              type="checkbox"
                              id="upload"
                              checked={isUploadLater}
                              value={isUploadLater}
                              onChange={handleCheckboxChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5">
        <div className="prev-btn p-1">
          <button type="button" className="btn btn-primary" onClick={prev}>
            {pagination.previous}
          </button>
        </div>
        {showSubmit && !isDocumentDone && (
          <div className="next-btn p-1">
            <button
              onClick={handleConfirm}
              type="button"
              className="btn btn-primary"
            >
              {buttonName.submit}
            </button>
          </div>
        )}
      </div>

      <AgreementModalId
        isOpen={addAgreementModal}
        toggle={showAgreementModal}
        anatureDataUrl={anatureDataUrl}
        loading={anatureLoading}
        error={anatureError}
      />
    </>
  );
};

export default Agreement;
